import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
    AnimatedPanel,
    SectionTitle,
    SectionDescription,
    CardsContainer,
    Card,
    CardImage,
    CardLabel,
    Small,
    HeadPanel,
    HeadTitle,
} from "./ui/StyledUI";
import { ReactComponent as SidebarButtonIcon } from "../../../../assets/images/icons/Sidebar.svg";
import { getAllNotes } from "../../../../features/guestbook/api/notesController";
import { CACHE_QUERY_OPTIONS } from "../../../../constants";

export default function OverviewContent({
    onSelectSection = () => {},
    imagesAndVideosUrls,
    setImagesAndVideosUrls,
    userId,
    roomId,
    interactionItems = [],
    roomType,
}) {
    const navigate = useNavigate();
    const [approved, setApproved] = React.useState(0);
    const [pending, setPending] = React.useState(0);

    const {
        data: notesData,
        error,
        isLoading,
        refetch: refetchNotes,
    } = useQuery(["notes", roomId], () => getAllNotes(roomId), {
        enabled: !!roomId,
        ...CACHE_QUERY_OPTIONS(),
    });

    React.useEffect(() => {
        if (notesData) {
            const approvedNotes = notesData.filter((note) => note.isApproved).length;
            const pendingNotes = notesData.filter((note) => !note.isApproved).length;
            setApproved(approvedNotes);
            setPending(pendingNotes);
        }
    }, [notesData]);

    // Track loading state of each image
    const [loadedImages, setLoadedImages] = React.useState({});
    const handleLoad = (key) => setLoadedImages((prev) => ({ ...prev, [key]: true }));

    // Reusable loader+image wrapper
    const renderImage = (key, src, alt) => (
        <ImageWrapper>
            {!loadedImages[key] && <LoaderBox>Loading...</LoaderBox>}

            <CardImage
                src={src}
                alt={alt}
                onLoad={() => handleLoad(key)}
                style={{
                    opacity: loadedImages[key] ? 1 : 0,
                    transition: "opacity 0.3s ease",
                }}
            />
        </ImageWrapper>
    );

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <StyledSidebarButtonIcon onClick={() => navigate(`/console/${userId}/my-rooms`)} />
                    <div>
                        <SectionTitle>Overview</SectionTitle>
                    </div>
                </div>

                {/* MEDIA FRAMES */}
                <AnimatedPanel>
                    <SubSectionTitle>Media Frames</SubSectionTitle>
                    <SectionDescription>Personalize your space by uploading custom pictures!</SectionDescription>

                    <CardsContainer>
                        <Card onClick={() => onSelectSection("Media Frames", "intraportPreview")}>
                            {renderImage("previewImg", imagesAndVideosUrls.previewImg.url, "Intraport preview")}
                            <CardLabel>Intraport Preview</CardLabel>
                        </Card>

                        <Card onClick={() => onSelectSection("Media Frames", "squareImageFrame")}>
                            {renderImage("squareImg", imagesAndVideosUrls.squareImg.url, "Square frame")}
                            <CardLabel>Square Image Frame</CardLabel>
                        </Card>

                        <Card onClick={() => onSelectSection("Media Frames", "verticalImageFrame1")}>
                            {renderImage("verticalImg1", imagesAndVideosUrls.verticalImg1.url, "Vertical 1")}
                            <CardLabel>Vertical Image Frame 1</CardLabel>
                        </Card>

                        <Card onClick={() => onSelectSection("Media Frames", "verticalImageFrame2")}>
                            {renderImage("verticalImg2", imagesAndVideosUrls.verticalImg2.url, "Vertical 2")}
                            <CardLabel>Vertical Image Frame 2</CardLabel>
                        </Card>

                        <Card onClick={() => onSelectSection("Media Frames", "videoFrame")}>
                            {renderImage("videoFrameImg", imagesAndVideosUrls.videoFrameImg.url, "Video Frame")}
                            <CardLabel>Video Frame</CardLabel>
                        </Card>
                    </CardsContainer>
                </AnimatedPanel>

                {/* INTERACTIONS */}
                <AnimatedPanel>
                    <SubSectionTitle>Interactions</SubSectionTitle>
                    <SectionDescription>Enhance objects with links, music, and more.</SectionDescription>

                    <CardsContainer>
                        {interactionItems.length === 0 && (
                            <Small>No interaction items available for this room type.</Small>
                        )}

                        {interactionItems.map((item) => (
                            <Card key={item.name} onClick={() => onSelectSection("Interactions", item.name)}>
                                {renderImage(item.name, item.img, item.name)}
                                <CardLabel>{item.name}</CardLabel>
                            </Card>
                        ))}
                    </CardsContainer>
                </AnimatedPanel>

                {/* GUESTBOOK */}
                <AnimatedPanel>
                    <SubSectionTitle>Guestbook</SubSectionTitle>
                    <SectionDescription>Manage the notes displayed in your guestbook.</SectionDescription>

                    <div style={{ display: "flex", gap: 18, alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <Small>
                                Pending: <strong style={{ marginLeft: 6 }}>{pending}</strong>
                            </Small>
                            <Small>
                                Published: <strong style={{ marginLeft: 6 }}>{approved}</strong>
                            </Small>
                        </div>
                    </div>
                </AnimatedPanel>
            </div>
        </>
    );
}

const SubSectionTitle = styled.h2`
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 6px 0;
`;

const StyledSidebarButtonIcon = styled(SidebarButtonIcon)`
    width: 35px;
    height: 35px;
    padding: 7px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 5px;
    margin-left: 5px;
    transition: transform 0.3s;
`;

const ImageWrapper = styled.div`
    width: 100%;
    position: relative;
    overflow: hidden;
    border-radius: 10px;
`;

const LoaderBox = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    justify-content: center;
    align-items: center;
    color: #aaa;
    font-size: 12px;
    border-radius: 10px;
    z-index: 5;
`;
